/**
 * Which device section of the help comes first. Pure: the web passes the user
 * agent and whether it runs installed (display-mode: standalone), the app its
 * platform from react-native.
 */
import type { HelpDevice } from './model';

/** How the help is being read. `browser` only for the web. */
export interface HelpContext {
  readonly device: HelpDevice;
  /** Running as the native app (iPhone, iPad, Android). */
  readonly app: boolean;
  /** Web only: which browser instructions fit best. */
  readonly browser?: 'ios-safari' | 'android-chrome' | 'desktop';
  /** Web only: opened from the home screen (PWA), not in a browser tab. */
  readonly installed?: boolean;
}

/** The native app: react-native's Platform.OS and Platform.isPad. */
export function nativeHelpContext(os: string, isPad: boolean): HelpContext {
  if (os === 'android') return { device: 'android', app: true };
  return { device: isPad ? 'ipad' : 'iphone', app: true };
}

/**
 * The web. iPadOS Safari reports itself as a Mac; a Mac with touch points is
 * an iPad. Everything the help says about the browser is in the `web` section;
 * `browser` only picks which of its sub-sections is shown first.
 */
export function webHelpContext(userAgent: string, standalone: boolean, maxTouchPoints = 0): HelpContext {
  const ua = userAgent.toLowerCase();
  const ipad = ua.includes('ipad') || (ua.includes('macintosh') && maxTouchPoints > 1);
  const ios = ipad || ua.includes('iphone') || ua.includes('ipod');
  const android = ua.includes('android');
  const browser = ios ? 'ios-safari' : android ? 'android-chrome' : 'desktop';
  return { device: 'web', app: false, browser, installed: standalone };
}

/** Device sections in the order the help lists them: the reader's own first. */
export function devicesInOrder(context: HelpContext): HelpDevice[] {
  const all: HelpDevice[] = ['iphone', 'ipad', 'android', 'web'];
  return [context.device, ...all.filter((d) => d !== context.device)];
}
